// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * ADR-085 never-bill-twice — the CLIENT REFUSAL RULE for the Whop checkout.
 *
 * `POST /v1/billing/checkout` refuses with a 409 when a Whop checkout must not
 * start (a live Apple subscription exists, a server free grant exists, the
 * Whop checkout has been retired, or the server no longer sells that plan). The Access screen used to treat ANY failure
 * of that call as "fall back to the static checkout link so checkout is never
 * blocked" — correct for a network error or a 5xx, but for a refusal it would
 * open a real Whop charge the server just said must not happen (a tab that
 * loaded its offers before an Apple purchase still holds the static link).
 *
 * This is the ONE decision. The screen opens the static link only when
 * `shouldFallBackToStaticCheckout(err)` is true; for a refusal it opens
 * nothing, says what is true, and reloads the offers.
 *
 * MIRROR: `mobile/src/lib/checkoutRefusal.ts` carries the same code verbatim
 * (the apps share no source package); `mobile/src/__tests__/checkout-refusal-
 * parity.test.ts` fails if the two drift. Keep them identical.
 *
 * The code comes from the typed client's `ApiError.envelope.code` (read
 * structurally, so a duplicated module copy cannot turn a refusal into a
 * fallback). Nothing here parses an error message.
 */
export const CHECKOUT_REFUSAL_CODES = [
  "APPLE_SUBSCRIPTION_ACTIVE",
  "FREE_PERIOD_ALREADY_USED",
  "CHECKOUT_RETIRED",
  // The server no longer sells the plan this stale screen still shows (billing.ts answers 409 OFFER_NOT_AVAILABLE).
  "OFFER_NOT_AVAILABLE",
] as const

export type CheckoutRefusalCode = (typeof CHECKOUT_REFUSAL_CODES)[number]

/** The refusal code carried by a failed checkout call, or null for any other failure. */
export function checkoutRefusalCode(err: unknown): CheckoutRefusalCode | null {
  if (typeof err !== "object" || err === null) return null
  const envelope = (err as { envelope?: unknown }).envelope
  if (typeof envelope !== "object" || envelope === null) return null
  const code = (envelope as { code?: unknown }).code
  return CHECKOUT_REFUSAL_CODES.find(known => known === code) ?? null
}

/** False ONLY for the refusal codes above; every other failure keeps today's static-link fallback. */
export function shouldFallBackToStaticCheckout(err: unknown): boolean {
  return checkoutRefusalCode(err) === null
}

/** i18n key of the consumer message for a refusal (no codes or internal terms are ever shown). */
export function checkoutRefusalMessageKey(code: CheckoutRefusalCode): string {
  switch (code) {
    case "APPLE_SUBSCRIPTION_ACTIVE":
      return "access.checkoutRefused.appleActive"
    case "FREE_PERIOD_ALREADY_USED":
      return "access.checkoutRefused.freeUsed"
    case "CHECKOUT_RETIRED":
      return "access.checkoutRefused.retired"
    case "OFFER_NOT_AVAILABLE":
      return "access.noOffer"
  }
}
